import { FEATURE_FLAGS } from '@constants';

import { can, hasAccountEntitlements } from '~/composables/useEntitlements';
import {
  accountAdminTargetFromQuery,
  isOpAccountAdminEligible,
  useAccountAdminAccess,
} from '~/composables/useAccountAdminAccess';
import { useAccountHierarchy } from '~/composables/useAccountHierarchy';
import { useFinanceVisibility } from '~/composables/useFinanceVisibility';
import { useLaunchDarkly } from '~/composables/useLaunchDarkly';
import { useAuthStore } from '~/store/auth';
import { useUserStore } from '~/store/user';

// Gates routes behind the caller's entitlements. valet is a SPA (ssr: false), so this runs only on
// the client. auth.authenticated is cookie-backed (available immediately). Returning navigateTo
// from the middleware body lets Nuxt perform the redirect before the blocked page renders, rather
// than deferring it into a callback. Unauthenticated redirects are handled upstream by
// auth.global.ts, which runs first (global middleware execute in alphabetical filename order).
export default defineNuxtRouteMiddleware(to => {
  const auth = useAuthStore();

  if (!auth.authenticated) return;

  // The ENG-5850 flag is a true kill-switch, including during the entitlement-hydration window
  // below. Check it first for direct hierarchy-dashboard loads; otherwise the intentional
  // entitlement fail-open would briefly make an off feature reachable on a cold load.
  const { getFlag } = useLaunchDarkly();
  const accountParentsEnabled = getFlag(FEATURE_FLAGS.ACCOUNT_PARENTS_ADMIN, false);
  const isHierarchyDashboardRoute = /^\/analytics\/(?:account|child-account)-dashboard(?:\/|$)/.test(to.path);

  if (isHierarchyDashboardRoute && !accountParentsEnabled) return navigateTo('/unauthorized');

  // Fail open ONLY while entitlements are unhydrated. Entitlements load via plugins/init.client.ts
  // (awaits auth.postLogin()); on a cold load straight to a gated route they may not be populated
  // when this runs, and can() would then short-circuit to false and bounce every authenticated
  // user to /unauthorized. The backend (analytics:read + StoreGuard) is the authoritative gate, so
  // skipping here is safe defense-in-depth — the guard enforces normally once entitlements exist
  // (every in-app navigation, and cold loads once the plugin resolves). Check `=== undefined`
  // (not loaded), not `!length`, so a genuinely-empty entitlements list still falls through to
  // can() and is correctly denied rather than failing open.
  const userStore = useUserStore();
  if (userStore.user?.entitlements === undefined) return;

  // A reseller-owned brand can have finance surfaces switched off entirely (ENG-5850); billing-svc
  // refuses the data either way, this just stops the page rendering on a direct URL.
  const {
    showFinance,
    showInvoices,
    showBillingSettings,
    showPolicies,
    showAccountDashboard,
    showChildAccountDashboard,
  } = useFinanceVisibility();
  const { isParent, parentAccountId } = useAccountHierarchy();
  const isInAccountHierarchy = isParent.value || !!parentAccountId.value;
  const parentAnalytics = hasAccountEntitlements([can.analytics.read]);
  // The reseller-only area (ENG-5850). /admin stays OP-staff-only and is gated separately below.
  // OP staff may enter too (Track 2D), but only carrying an explicit ?accountId= hand-off context.
  const accountAdminTarget = accountAdminTargetFromQuery(to.query);
  const canAccessAccountAdmin = useAccountAdminAccess(() => accountAdminTarget);
  const opAccountAdminEligible = isOpAccountAdminEligible({
    accountEntitlements: userStore.user?.entitlements,
    roleName: userStore.user?.role?.name,
  });

  // First matching regex wins, so authorization behavior depends on this insertion order.
  /* eslint-disable sort-keys-fix/sort-keys-fix */
  const Routes = {
    // Real paths: the invoices list lives under /finance/payments, not /finance/invoices.
    // Anchored, unlike the sibling keys: these are matched as unanchored regexes, so an unanchored
    // key would also match unrelated paths containing the same word and, being first, would beat
    // '/admin/*' — redirecting OP staff off their own pages.
    '^/account-admin': [canAccessAccountAdmin.value],
    // A parent's dashboards span every child, so they need account-level analytics, not one brand's store role.
    '^/analytics/account-dashboard(?:/|$)': [
      accountParentsEnabled && parentAnalytics && isParent.value && showAccountDashboard.value,
    ],
    '^/analytics/child-account-dashboard(?:/|$)': [
      accountParentsEnabled &&
        can(can.analytics.read) &&
        (!isParent.value || parentAnalytics) &&
        isInAccountHierarchy &&
        showChildAccountDashboard.value,
    ],
    // Anchored and listed before the broader payments key (first match wins): the Invoices tab lives
    // under /finance/payments, so a brand with showInvoices off must be caught here — the payments
    // key only checks showFinance, so otherwise the tab is reachable by URL and skips the
    // hidden-by-account-admin copy.
    '^/[^/]+/finance/payments/invoices(?:/|$)': [can(can.invoice.read) && showInvoices.value],
    '/*/finance/payments': [can(can.invoice.read) && showFinance.value],
    '/*/finance/payouts': [can(can.payout.read) && showFinance.value],
    '/*/marketplace/*': [can(can.marketplace.read)],
    '/*/orders': [can(can.order.read)],
    '/*/payouts': [can(can.payout.read) && showFinance.value],
    '/*/settings/api-tokens': [can(can.apiTokens.read)],
    '/*/settings/billing': [can(can.billing.read) && showBillingSettings.value],
    '/*/settings/contact': [can(can.contact.read)],
    '/*/settings/policies': [can(can.policies.read) && showPolicies.value],
    '/*/settings/general': [can(can.settingsGeneral.read)],
    '/*/settings/webhooks': [can(can.webhook.read)],
    '/*/team-members': [can(can.teamMember.read)],
    '/*/tests': [can(can.abTests.read)],
    '/admin/*': [can(can.adminStores.read), can(can.adminUsers.read), can(can.whitelabel.list)],
    '/claims/*': [can(can.claim.read)],
    '^/customers(?:/|$)': [can(can.customers.read)],
    '/dashboard': [can(can.analytics.read)],
    '/developer/*': [can(can.developerApp.read)],
  } as Record<string, boolean[]>;
  /* eslint-enable sort-keys-fix/sort-keys-fix */

  // Which route keys can be switched off by a reseller rather than by entitlements — used below to
  // pick the "hidden by your administrator" copy instead of the generic unauthorized wording.
  const VISIBILITY_FOR_ROUTE: Record<string, () => boolean> = {
    '/*/finance/payments': () => showFinance.value,
    '/*/finance/payouts': () => showFinance.value,
    '/*/payouts': () => showFinance.value,
    '/*/settings/billing': () => showBillingSettings.value,
    '/*/settings/policies': () => showPolicies.value,
    '^/[^/]+/finance/payments/invoices(?:/|$)': () => showInvoices.value,
    '^/analytics/account-dashboard(?:/|$)': () => showAccountDashboard.value,
    '^/analytics/child-account-dashboard(?:/|$)': () => showChildAccountDashboard.value,
  };
  const SUPPRESSIBLE_ROUTES = Object.keys(VISIBILITY_FOR_ROUTE);

  const route = Object.keys(Routes).find(route => new RegExp(`${route}`).test(to.path));

  if (!route) return; // No permissions required

  const permissions = Routes[route];

  if (!permissions || !permissions.length) return; // No permissions required

  const hasPermission = permissions.some(permission => !!permission);

  if (hasPermission) return;

  // An OP staffer without a hand-off context is not unauthorized — they just have not picked a
  // tree. Send them to Admin → Stores, where the "Manage in Account Admin" links carry the context.
  if (route === '^/account-admin' && opAccountAdminEligible) return navigateTo('/admin/stores');

  // Distinguish "your administrator hid this" from "you lack permission" (ENG-5850 §6.4). The user
  // holds the entitlement but their account's visibility settings switched the surface off, so the
  // page must read as a deliberate wall rather than an error.
  const hiddenByAccountSettings = SUPPRESSIBLE_ROUTES.some(
    key => route === key && VISIBILITY_FOR_ROUTE[key]?.() === false,
  );

  return navigateTo(hiddenByAccountSettings ? '/hidden-by-account-admin' : '/unauthorized');
});
